import { useNotificationContext } from 'contexts/NotificationContext';
import { type RefObject, useEffect } from 'react';
import { displayError } from 'utils/api';
import { type RowErrorSource, useMissionControlStore } from '../useMissionControlStore';
import { type QueueRow, type QueueRowKey } from './queueRowKey';

/**
 * Mutation `meta` marking a mutation that reports its failures on queue rows, so
 * that `SaveStatus` shows them as failed only while their row errors are open.
 */
export const ROW_ERRORS_META = { reportsRowErrors: true } as const;

export function reportsRowErrors(meta: Record<string, unknown> | undefined): boolean {
    return meta?.reportsRowErrors === true;
}

type RowErrorReport = {
    /** its `label` names the row in the toast shown while it's off screen */
    row: QueueRow;
    source: RowErrorSource;
    /** what failed, e.g. "Failed to acknowledge 3 alerts" */
    summary: string;
    error: Error;
};

/**
 * Reports failed actions on queue rows, shown under the row until closed (see
 * `RowErrors`). A row that's off screen when its error is reported also gets a
 * toast.
 *
 * The returned functions keep working after the calling component unmounts, so
 * they can be called from a mutation's `onError` / `onSuccess`.
 */
export function useRowErrorReporter() {
    const { reportRowError, clearRowError, getRowElement } = useMissionControlStore(
        (s) => ({
            reportRowError: s.reportRowError,
            clearRowError: s.clearRowError,
            getRowElement: s.getRowElement,
        }),
    );
    const { showToast } = useNotificationContext();

    const report = ({ row, source, summary, error }: RowErrorReport) => {
        const message = `${summary}: ${displayError(error) ?? 'Unknown error'}`;
        reportRowError(row.key, { source, message });

        const element = getRowElement(row.key);
        void (element ? isOnScreen(element) : Promise.resolve(false)).then((onScreen) => {
            if (!onScreen) {
                showToast({ type: 'error', content: `${row.label}: ${message}` });
            }
        });
    };

    /** Clears the row's error from `source`, e.g. once its action succeeds. */
    const clear = (rowKey: QueueRowKey, source: RowErrorSource) =>
        clearRowError(rowKey, source);

    return { report, clear };
}

/** Registers a row's element, so `useRowErrorReporter` can tell if it's on screen. */
export function useRowElement(rowKey: QueueRowKey, ref: RefObject<HTMLElement>) {
    const { setRowElement } = useMissionControlStore((s) => ({
        setRowElement: s.setRowElement,
    }));
    useEffect(() => {
        setRowElement(rowKey, ref.current);
        return () => setRowElement(rowKey, null);
    }, [rowKey, ref, setRowElement]);
}

/**
 * Whether any of `element` is visible in the viewport right now, clipped by its
 * scrolling ancestors. Observes only until the first answer, rather than
 * tracking visibility continuously.
 */
function isOnScreen(element: Element): Promise<boolean> {
    if (!element.isConnected) {
        return Promise.resolve(false);
    }
    return new Promise((resolve) => {
        const observer = new IntersectionObserver(([entry]) => {
            observer.disconnect();
            resolve(entry.isIntersecting);
        });
        observer.observe(element);
    });
}
